/**
 * Cryptography for the RustDesk session protocol.
 *
 * The handshake, verbatim from the client's secure_connection():
 *
 *   1. The rendezvous server hands us `pk`, which is
 *      sign(IdPk{id, pk}, server_secret_key). We verify it with the server's
 *      public key (the deployment `key`) and learn the peer's *signing* key.
 *   2. The peer sends Message.SignedId { id: sign(IdPk{...}, peer_secret) }.
 *      Verified with the key from step 1, it yields the peer's X25519 *box*
 *      public key. This is what authenticates the peer.
 *   3. We generate a random secretbox key, seal it to the peer's box key with
 *      a zero nonce, and send Message.PublicKey{asymmetric_value: our box pk,
 *      symmetric_value: sealed}.
 *   4. Both sides then frame every message with XSalsa20-Poly1305 using a
 *      per-direction counter as the nonce.
 *
 * Because step 1 is anchored in the server key and step 2 in the peer key, a
 * relay (including our own ws bridge) only ever sees ciphertext.
 */
import nacl from "tweetnacl";
export const SIGN_PUBLICKEY_BYTES = 32;
export const BOX_PUBLICKEY_BYTES = 32;
export const SECRETBOX_KEY_BYTES = 32;
const NONCE_BYTES = 24;
export function fromBase64(s) {
    const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/"));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
}
export function toBase64(b) {
    let s = "";
    for (const byte of b)
        s += String.fromCharCode(byte);
    return btoa(s);
}
/**
 * Opens a NaCl signed message (signature || payload).
 * Returns null when the signature does not verify.
 */
export function signOpen(signed, signPublicKey) {
    if (signPublicKey.length !== SIGN_PUBLICKEY_BYTES)
        return null;
    try {
        return nacl.sign.open(signed, signPublicKey);
    }
    catch {
        return null;
    }
}
/**
 * Mirrors create_symmetric_key_msg() in the client's common.rs.
 * The zero nonce is safe here because the box keypair is ephemeral and used
 * exactly once.
 */
export function createSymmetricKeyMsg(theirBoxPk) {
    if (theirBoxPk.length !== BOX_PUBLICKEY_BYTES) {
        throw new Error(`peer box key must be ${BOX_PUBLICKEY_BYTES} bytes, got ${theirBoxPk.length}`);
    }
    const ours = nacl.box.keyPair();
    const key = nacl.randomBytes(SECRETBOX_KEY_BYTES);
    const nonce = new Uint8Array(NONCE_BYTES); // all zero, as upstream
    const sealed = nacl.box(key, nonce, theirBoxPk, ours.secretKey);
    return { asymmetric: ours.publicKey, symmetric: sealed, key };
}
/** Builds the 24-byte nonce from a sequence number (LE u64, zero-padded). */
export function nonceFromSeq(seq) {
    const nonce = new Uint8Array(NONCE_BYTES);
    let v = seq;
    for (let i = 0; i < 8; i++) {
        nonce[i] = Number(v & 0xffn);
        v >>= 8n;
    }
    return nonce;
}
/**
 * Per-connection stream cipher state.
 *
 * Send and receive have independent counters, both starting at 0 and
 * pre-incremented, so the first message in each direction uses nonce 1. This
 * matches `Encrypt` in hbb_common's tcp.rs exactly.
 */
export class StreamCipher {
    #key;
    #sendSeq = 0n;
    #recvSeq = 0n;
    constructor(key) {
        if (key.length !== SECRETBOX_KEY_BYTES)
            throw new Error("bad session key length");
        this.#key = key;
    }
    seal(plain) {
        this.#sendSeq += 1n;
        // nacl always allocates a fresh, non-shared buffer.
        return nacl.secretbox(plain, nonceFromSeq(this.#sendSeq), this.#key);
    }
    /**
     * Opens a frame. Frames of 0 or 1 bytes are passed through untouched: the
     * upstream `dec` short-circuits on `len <= 1`, which is how the empty
     * keep-alive Message survives an encrypted stream.
     */
    open(data) {
        if (data.length <= 1)
            return data;
        this.#recvSeq += 1n;
        const out = nacl.secretbox.open(data, nonceFromSeq(this.#recvSeq), this.#key);
        return out ?? null;
    }
}
/** SHA-256 over the concatenation of its arguments. */
export async function sha256(...parts) {
    let total = 0;
    const bufs = parts.map((p) => (typeof p === "string" ? new TextEncoder().encode(p) : p));
    for (const b of bufs)
        total += b.length;
    const joined = new Uint8Array(total);
    let off = 0;
    for (const b of bufs) {
        joined.set(b, off);
        off += b.length;
    }
    const digest = await crypto.subtle.digest("SHA-256", joined);
    return new Uint8Array(digest);
}
/**
 * The two-stage password hash the peer expects.
 *
 *   h1 = sha256(password || salt)     <- this is what an address book stores
 *   h2 = sha256(h1 || challenge)      <- this is what LoginRequest carries
 *
 * Passing `h1` directly (from a saved address book entry) skips the first
 * stage, which is exactly what the desktop client does.
 */
export async function hashPasswordForLogin(password, salt, challenge) {
    const h1 = await sha256(password, salt);
    return sha256(h1, challenge);
}
export async function hashStoredPasswordForLogin(storedHash, challenge) {
    return sha256(storedHash, challenge);
}
/** h1 only: what gets stored in an address book entry's `hash` field. */
export async function hashPasswordForStorage(password, salt) {
    return sha256(password, salt);
}
